import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import * as yup from 'yup';

import { createUrl, request } from 'app/utils';
import { AxiosError } from 'axios';
import { Button, ButtonIcon, Flex, HelperText, Icons, Loading, Page, Typography } from 'components';
import Alert, { useAlert } from 'features/alert';
import { AuthApiResponse, AuthenticationResultCode } from 'features/auth';
import { useSchool } from 'features/school';
import { useNavigate } from 'react-router-dom';
import { CenteredLayout, EducationalInput } from '../components';
import useEducational from '../useEducational';

const schema = yup.object({
  password: yup.string().required('Você precisa informar uma senha de acesso.'),
});

const ArrowIcon = styled(Icons)`
  width: 24px;
  color: ${({ theme }) => theme.colors.white};
`;

const ButtonShowPassword = styled(ButtonIcon)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 15px;
`;

const ResetPassword = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [waiting, setWaiting] = useState(false);
  const [showPassword, toggleShowPassword] = useState(false);

  const { apiGatewayUrl, tenantUuid } = useSchool();
  const { activatedCode } = useEducational();
  const { setAlert, clearAlert } = useAlert();

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<{ password: string }>({
    resolver: yupResolver(schema),
  });

  const onSubmit = useCallback(
    async ({ password }: { password: string }) => {
      clearAlert();
      setWaiting(true);

      request<AuthApiResponse>(
        {
          url: '/api/v1/User/PasswordChange',
          method: 'post',
          baseURL: apiGatewayUrl,
          data: {
            newPassword: password,
            recoveryToken: activatedCode.activatedUserGeneratedRecoveryToken,
            tenantUuid,
          },
        },
        true
      )
        .then(({ data }) => {
          reset();

          navigate('/login');

          setTimeout(() => {
            setAlert({
              type: 'success',
              message: '<strong>Sua senha foi definida com sucesso.</strong><br />Você já pode acessar a plataforma.',
            });
          }, 50);
        })
        .catch(
          (
            error: AxiosError<{
              authenticationResult: number;
              message: string;
            }>
          ) => {
            let message = 'Não foi possível redefinir sua senha.<br />Por favor, tente tovamente.';

            if (error?.response?.data?.message) {
              message = error?.response?.data?.message;
            }

            if (error?.response?.data?.authenticationResult) {
              switch (error?.response?.data?.authenticationResult) {
                case AuthenticationResultCode.InvalidRecoveryToken:
                  message =
                    "Token de recuperação inválido. Por favor, Por favor, crie uma senha nova através do link 'Esqueci a senha', localizado logo abaixo";
                  break;
                default:
                  break;
              }
            }

            setAlert({
              type: 'error',
              message,
            });
          }
        )
        .finally(() => {
          setWaiting(false);
        });
    },
    [activatedCode, tenantUuid]
  );

  useEffect(() => {
    if (activatedCode) {
      setLoading(false);
    } else {
      navigate(createUrl('educational/activate/code'));
    }
  }, [activatedCode]);

  if (loading) return <Loading />;

  return (
    <Page title="Alterar senha - MELVER nas Escolas">
      {waiting && <Loading alpha />}

      <form onSubmit={handleSubmit(onSubmit)}>
        <CenteredLayout maxWidth={{ m: '90%', t: 550 }} minWidth={280}>
          <Flex flexDirection="column" alignItems="center" style={{ gap: 10 }}>
            <Flex flexDirection={'column'} alignItems={'center'}>
              <Flex flexDirection="column" justifyContent="center" textAlign="center" style={{ gap: 5 }}>
                <Typography type="type4" fontWeight="bold">
                  Alterar senha
                </Typography>
                <Typography>Digite uma nova senha de acesso à nossa plataforma.</Typography>
              </Flex>
            </Flex>

            <Flex width={{ m: 1, d: '80%' }} maxWidth={{ m: '100%', d: 300 }} flexGrow={0} flexDirection="column">
              <Flex flexDirection="column" position="relative">
                <ButtonShowPassword
                  onClick={() => toggleShowPassword((prev) => !prev)}
                  type="button"
                  icon={showPassword ? 'eyeOff' : 'eye'}
                  tabIndex={3}
                  color="gray_400"
                  bg="transparent"
                  iconWidth={22}
                />

                <EducationalInput type={showPassword ? 'text' : 'password'} {...register('password')} style={{ paddingRight: 45 }} />
              </Flex>

              {errors?.password && <HelperText type="error">{errors.password.message}</HelperText>}
            </Flex>

            <HelperText>
              Por segurança, sua senha deve conter no mínimo 8 caracteres sendo pelo menos 1 letra maiúscula, 1 letra minúscula, 1 número e um
              caractere especial (%?!@#$&*).
            </HelperText>
          </Flex>

          <div style={{ textAlign: 'center' }}>
            <Alert invert style={{ fontWeight: 'bold' }} py={0} px={0} />
          </div>

          <Flex>
            <Button type="submit" py={4} width={1} px={3} color="white" justifyContent="space-between" style={{ gap: 20 }} disabled={isSubmitting}>
              <>Confirmar senha</>
              <ArrowIcon icon="arrowRightThin" />
            </Button>
          </Flex>
        </CenteredLayout>
      </form>
    </Page>
  );
};

export default ResetPassword;
